import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Toaster } from 'react-hot-toast';

import { agentManagementService } from '../../services/agentManagementService';
import { subscriptionService } from '../../services/subscriptionService';
import NavigationSidebar from '../../components/NavigationSidebar';

import Button from '../../components/ui/Button';
import SubscriptionOverview from './components/SubscriptionOverview';
import AgentControlCard from './components/AgentControlCard';
import DowngradeAssistant from './components/DowngradeAssistant';
import AgentCapacityOverview from './components/AgentCapacityOverview';

const AgentManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [agentStatus, setAgentStatus] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showDowngradeAssistant, setShowDowngradeAssistant] = useState(false);
  const [agentAnalytics, setAgentAnalytics] = useState([]);

  const agentsConfig = [
    {
      id: 'marketing',
      dbId: 'marketing-ops',
      name: 'Marketing Operations',
      description: 'Creates campaigns, ads, and marketing content',
      icon: 'TrendingUp',
      color: 'blue',
      index: 0
    },
    {
      id: 'productivity',
      dbId: 'personal-productivity',
      name: 'Personal Productivity',
      description: 'Task management and daily checklists',
      icon: 'CheckSquare',
      color: 'emerald',
      index: 1
    },
    {
      id: 'feedback',
      dbId: 'feedback-analytics',
      name: 'Feedback & Analytics',
      description: 'Customer feedback analysis and insights',
      icon: 'BarChart3',
      color: 'orange',
      index: 2
    },
    {
      id: 'onboarding',
      dbId: 'onboarding',
      name: 'Onboarding Agent',
      description: 'Setup and configuration assistance',
      icon: 'Rocket',
      color: 'rose',
      index: 3
    }
  ];

  useEffect(() => {
    if (user?.id) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusResult, subResult, analyticsResult] = await Promise.all([
        agentManagementService?.getAgentStatus(user?.id),
        subscriptionService?.getUserSubscription(user?.id),
        agentManagementService?.getAgentAnalytics(user?.id)
      ]);

      if (statusResult?.data) {
        setAgentStatus(statusResult?.data);
      }
      if (subResult?.data) {
        setSubscription(subResult?.data);
      }
      if (analyticsResult?.data) {
        setAgentAnalytics(analyticsResult?.data);
      }

      // Check if downgrade assistant should be shown
      if (statusResult?.data?.limitStatus?.grace_period_end) {
        setShowDowngradeAssistant(true);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAgent = async (agentDbId, currentlyActive) => {
    setProcessing(true);
    try {
      const { error } = await agentManagementService?.toggleAgent(
        user?.id,
        agentDbId,
        !currentlyActive
      );

      if (error) {
        throw new Error(error?.message || 'Failed to toggle agent');
      } else {
        await loadData();
      }
    } catch (error) {
      console.error('Error toggling agent:', error);
      throw error;
    } finally {
      setProcessing(false);
    }
  };

  const handleDowngradeConfirm = async (selectedAgentIds) => {
    // Process deactivation for selected agents
    const results = await Promise.allSettled(
      selectedAgentIds?.map(agentId => {
        const agentConfig = agentsConfig?.find(a => a?.id === agentId);
        if (agentConfig) {
          return agentManagementService?.toggleAgent(user?.id, agentConfig?.dbId, false);
        }
        return Promise.reject(new Error('Agent not found'));
      })
    );

    const failed = results?.filter(r => r?.status === 'rejected');
    if (failed?.length > 0) {
      throw new Error(`Failed to deactivate ${failed.length} agent(s)`);
    }

    await loadData();
    setShowDowngradeAssistant(false);
  };

  const getAgentData = (agentConfig) => {
    const agentData = agentStatus?.agents?.find(a => a?.agentId === agentConfig?.dbId);
    const tierReq = agentManagementService?.getTierRequirement(agentConfig?.index);
    const currentTier = subscription?.tier || 'free';
    const canActivate = agentManagementService?.canActivateAgent(currentTier, tierReq?.tier);
    const analytics = agentAnalytics?.find(a => a?.agentId === agentConfig?.dbId);

    return {
      ...agentConfig,
      isActive: agentData?.isActive || false,
      lastUsed: agentData?.lastUsed,
      tierRequirement: tierReq,
      canActivate,
      usageImpact: agentManagementService?.getUsageImpact(agentConfig?.dbId),
      analytics
    };
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <NavigationSidebar />
        <div className="flex-1 flex items-center justify-center ml-0 lg:ml-60">
          <p className="text-muted-foreground">Loading agent management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <Toaster position="top-right" />
      <NavigationSidebar />
      <main className="flex-1 ml-0 lg:ml-60 pb-20 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground font-heading mb-2">
                Agent Management
              </h1>
              <p className="text-muted-foreground">
                Control agent activation, view usage, and manage subscription compliance
              </p>
            </div>
            <Button
              variant="outline"
              size="md"
              onClick={() => navigate('/dashboard')}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Back to Dashboard
            </Button>
          </div>

          {/* Agent Capacity Overview */}
          <AgentCapacityOverview
            agentStatus={agentStatus}
            subscription={subscription}
            agentsConfig={agentsConfig}
          />

          {/* Subscription Overview */}
          <SubscriptionOverview
            limitStatus={agentStatus?.limitStatus}
            subscription={subscription}
            onUpgrade={() => navigate('/pricing')}
          />

          {/* Downgrade Assistant */}
          {showDowngradeAssistant && agentStatus?.limitStatus?.grace_period_end && (
            <DowngradeAssistant
              limitStatus={agentStatus?.limitStatus}
              agents={agentsConfig?.map(getAgentData)}
              onClose={() => setShowDowngradeAssistant(false)}
              onConfirm={handleDowngradeConfirm}
            />
          )}

          {/* Agent Control Cards */}
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-4 font-heading">
              Your AI Agents
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {agentsConfig?.map((agentConfig) => {
                const agentData = getAgentData(agentConfig);
                return (
                  <AgentControlCard
                    key={agentConfig?.id}
                    agent={agentData}
                    onToggle={() => handleToggleAgent(agentConfig?.dbId, agentData?.isActive)}
                    disabled={processing || (!agentData?.canActivate && !agentData?.isActive)}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AgentManagement;